import React from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/ui/components/ui/avatar";
import {
  Bell,
  Flame,
  TrendingUp,
  Clock,
  CheckCircle2,
  Target,
  ArrowRight,
  Trophy,
  Lock,
} from "lucide-react";
import { getTempleIcon } from "@/utils/temple-icons";
import { cn } from "@/utils/utils";

import { Temple as TempleBase } from "@/data/gamification";

interface Temple extends TempleBase {
  progress_percentage?: number;
  status?: "locked" | "unlocked" | "in_progress" | "completed";
  is_featured?: boolean;
}

interface MobileDashboardProps {
  user: {
    full_name: string;
    level: number;
    xp: number;
    total_progress_percentage: number;
    streak_days: number;
    avatar_url?: string;
  };
  temples: Temple[];
  notifications: {
    unread_count: number;
    recent_reminders: Array<{
      title: string;
      message: string;
    }>;
  };
  epicGoal: {
    progress_percentage: number;
  };
  onTempleClick: (temple: Temple) => void;
}

export function MobileDashboard({
  user,
  temples,
  notifications,
  epicGoal,
  onTempleClick,
}: MobileDashboardProps) {
  const navigate = useNavigate();

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Bom dia";
    if (hour < 18) return "Boa tarde";
    return "Boa noite";
  };

  const firstName = user.full_name.split(" ")[0];
  const featuredTemple = temples.find((t) => t.is_featured);
  const otherTemples = temples.filter((t) => !t.is_featured);

  return (
    <div className="min-h-screen bg-[#fafaf9] pb-24">
      {/* Header */}
      <div className="bg-white border-b border-black/[0.06] px-4 py-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10 ring-[1.5px] ring-black/[0.06] ring-offset-[1.5px] ring-offset-white">
              <AvatarImage src={user.avatar_url} className="object-cover" />
              <AvatarFallback className="bg-gradient-to-br from-accent-800 to-accent-600 text-white text-xs font-bold">
                {getInitials(user.full_name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-semibold text-neutral-800">
                {firstName}
              </p>
              <p className="text-xs text-neutral-400 flex items-center gap-1.5">
                <span className="text-[10px] font-bold text-accent-800 bg-accent-800/[0.07] px-1.5 py-[1px] rounded">
                  Nv {user.level}
                </span>
                <span className="font-medium tabular-nums">{user.xp} XP</span>
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/notificacoes")}
            className="relative w-10 h-10 rounded-xl bg-black/[0.03] flex items-center justify-center"
            aria-label="Notificações"
          >
            <Bell className="w-[18px] h-[18px] text-neutral-400" />
            {notifications.unread_count > 0 && (
              <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 bg-red-500 text-white text-[8px] rounded-full flex items-center justify-center font-bold px-0.5">
                {notifications.unread_count}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="px-4 pt-5 space-y-4">
        {/* Greeting */}
        <div>
          <h1 className="text-xl font-bold text-neutral-800 tracking-tight">
            {getGreeting()},{" "}
            <span className="text-accent-800">{firstName}</span>
          </h1>
          <p className="text-sm text-neutral-400 mt-0.5">
            Continue sua jornada rumo ao legado.
          </p>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-3 gap-2.5">
          {[
            {
              icon: Flame,
              value: user.streak_days,
              label: "Streak",
              color: "text-orange-500",
              bg: "bg-orange-50",
            },
            {
              icon: Target,
              value: `${Math.round(epicGoal.progress_percentage)}%`,
              label: "Meta",
              color: "text-accent-800",
              bg: "bg-accent-800/[0.06]",
            },
            {
              icon: TrendingUp,
              value: `${Math.round(user.total_progress_percentage)}%`,
              label: "Progresso",
              color: "text-emerald-500",
              bg: "bg-emerald-50",
            },
          ].map((metric) => (
            <div
              key={metric.label}
              className="bg-white rounded-xl border border-black/[0.06] p-3 text-center shadow-[0_1px_2px_rgba(0,0,0,0.02)]"
            >
              <div
                className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-1.5",
                  metric.bg,
                )}
              >
                <metric.icon className={cn("w-4 h-4", metric.color)} />
              </div>
              <p className="text-lg font-bold text-neutral-800 tabular-nums leading-tight">
                {metric.value}
              </p>
              <p className="text-[9px] text-neutral-400 font-semibold uppercase tracking-[0.08em] mt-0.5">
                {metric.label}
              </p>
            </div>
          ))}
        </div>

        {/* Reminders */}
        {notifications.recent_reminders.length > 0 && (
          <button
            onClick={() => navigate("/notificacoes")}
            className="w-full text-left"
          >
            <div className="bg-white rounded-xl border border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.02)] overflow-hidden">
              <div className="flex">
                <div className="w-[3px] bg-accent-800 flex-shrink-0" />
                <div className="flex items-center gap-3 p-3.5 flex-1">
                  <div className="w-8 h-8 bg-accent-800/[0.06] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Bell className="w-[14px] h-[14px] text-accent-800" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-neutral-800 mb-0.5">
                      {notifications.recent_reminders.length}{" "}
                      {notifications.recent_reminders.length === 1
                        ? "lembrete"
                        : "lembretes"}{" "}
                      hoje
                    </p>
                    <p className="text-xs text-neutral-400 truncate">
                      {notifications.recent_reminders[0].message}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-neutral-300 flex-shrink-0" />
                </div>
              </div>
            </div>
          </button>
        )}

        {/* Epic Goal */}
        <div className="bg-white rounded-xl border border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.02)] overflow-hidden">
          <div className="h-[2px] bg-gradient-to-r from-accent-800 via-accent-600 to-accent-800/20" />
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-bold text-accent-800 bg-accent-800/[0.07] px-1.5 py-[2px] rounded uppercase tracking-[0.06em]">
                  Meta Épica
                </span>
              </div>
              <span className="flex items-center gap-1 text-xs text-neutral-400">
                <span className="w-1.5 h-1.5 bg-accent-800 rounded-full" />
                Em progresso
              </span>
            </div>
            <p className="text-sm font-bold text-neutral-800 mb-1">
              Conquista do SIM
            </p>
            <p className="text-xs text-neutral-400 mb-3">
              7 semanas de jornada
            </p>
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-neutral-400">Progresso</span>
                <span className="font-bold text-accent-800 tabular-nums">
                  {Math.round(epicGoal.progress_percentage)}%
                </span>
              </div>
              <div className="h-[5px] bg-black/[0.04] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-accent-800 to-accent-600 rounded-full transition-all duration-500"
                  style={{ width: `${epicGoal.progress_percentage}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Temples */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-neutral-800">Seus Templos</h2>
            <button
              className="text-xs text-accent-800/80 font-semibold hover:text-accent-800 transition-colors"
              onClick={() => navigate("/jornada")}
            >
              Ver todos
            </button>
          </div>

          {/* Featured Temple */}
          {featuredTemple && (
            <div
              className="mb-3 bg-white rounded-xl border border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.02)] overflow-hidden active:scale-[0.98] transition-transform"
              onClick={() => onTempleClick(featuredTemple)}
            >
              <div className="h-[2px] bg-gradient-to-r from-accent-800 via-accent-600 to-accent-800/20" />
              <div className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
                    style={{ backgroundColor: featuredTemple.theme_color }}
                  >
                    {React.createElement(getTempleIcon(featuredTemple.icon), {
                      className: "w-5 h-5 text-white",
                    })}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="text-sm font-bold text-neutral-800 truncate">
                        {featuredTemple.name}
                      </h3>
                      <span className="text-[8px] font-bold text-accent-800 bg-accent-800/[0.07] px-1.5 py-[1px] rounded uppercase tracking-wider flex-shrink-0">
                        Destaque
                      </span>
                    </div>
                    <p className="text-xs text-neutral-400 line-clamp-1">
                      {featuredTemple.description}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <div className="flex-1 h-[4px] bg-black/[0.04] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent-800 rounded-full transition-all duration-500"
                      style={{
                        width: `${featuredTemple.progress_percentage || 0}%`,
                      }}
                    />
                  </div>
                  <span className="text-[11px] font-bold text-accent-800 tabular-nums">
                    {Math.round(featuredTemple.progress_percentage || 0)}%
                  </span>
                </div>

                <div className="flex items-center gap-3 mt-2.5 text-xs text-neutral-400">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    {featuredTemple.total_tasks} tarefas
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />1 semana
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Other Temples */}
          <div className="space-y-2">
            {otherTemples.map((temple) => {
              const isLocked = temple.status === "locked";
              const isCompleted = temple.status === "completed";
              const TempleIcon = getTempleIcon(temple.icon);
              const progress = Math.round(temple.progress_percentage || 0);

              return (
                <div
                  key={temple.id}
                  className={cn(
                    "bg-white rounded-xl border border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-all",
                    isLocked ? "opacity-50" : "active:scale-[0.98]",
                  )}
                  onClick={() => !isLocked && onTempleClick(temple)}
                >
                  <div className="p-3.5">
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0",
                          isLocked
                            ? "bg-neutral-200"
                            : "shadow-[0_1px_3px_rgba(0,0,0,0.1)]",
                        )}
                        style={
                          !isLocked
                            ? { backgroundColor: temple.theme_color }
                            : undefined
                        }
                      >
                        {isLocked ? (
                          <Lock className="w-4 h-4 text-neutral-400" />
                        ) : isCompleted ? (
                          <CheckCircle2 className="w-5 h-5 text-white" />
                        ) : (
                          <TempleIcon className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4
                          className={cn(
                            "text-sm font-semibold",
                            isLocked ? "text-neutral-400" : "text-neutral-800",
                          )}
                        >
                          {temple.name}
                        </h4>
                        {!isLocked && progress > 0 ? (
                          <div className="flex items-center gap-2 mt-1.5">
                            <div className="flex-1 h-[3px] bg-black/[0.04] rounded-full overflow-hidden">
                              <div
                                className={cn(
                                  "h-full rounded-full transition-all duration-500",
                                  isCompleted
                                    ? "bg-emerald-500"
                                    : "bg-accent-800",
                                )}
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                            <span
                              className={cn(
                                "text-[10px] font-bold tabular-nums",
                                isCompleted
                                  ? "text-emerald-500"
                                  : "text-accent-800",
                              )}
                            >
                              {progress}%
                            </span>
                          </div>
                        ) : !isLocked && progress === 0 ? (
                          <p className="text-xs text-neutral-400 mt-0.5">
                            {temple.total_tasks} tarefas
                          </p>
                        ) : (
                          <p className="text-xs text-neutral-400 mt-0.5">
                            Bloqueado
                          </p>
                        )}
                      </div>
                      {!isLocked && progress === 0 && (
                        <ArrowRight className="w-4 h-4 text-neutral-300 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
